import { FC } from 'react';
import {
  ActionsMenu,
  PageContentLayout,
  PageHeaderLayout,
  Link,
  ListTotal,
  SideMenu,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { APP, ROUTES } from 'config';
import {
  COLOR_BY_STATUS,
  surveysFiltersSchema,
  surveyStatuses,
  useSurveys,
} from 'store/surveys';
import { useJobsContext } from 'store/jobs';
import { useModalContext } from 'store/modal';
import {
  CreateSurveyModal,
  GenerateTrainingModal,
  SurveyActions,
} from 'containers/surveys';
import { formatPercents } from 'utils/format';
import { useRouteMatch } from 'react-router-dom';
import { Divider, Stack } from '@mui/material';
import { useSearchParams } from 'utils/navigation';
import {
  DateRangeFilter,
  FilterButton,
  Filters,
  SelectFilter,
} from 'components/filters';
import { AutoAwesomeOutlined } from '@mui/icons-material';
import { useMe } from 'state/Administrators';

export const TrainingsList: FC = () => {
  const { t, i18n } = useTranslation();
  const { openModal } = useModalContext();
  const { inProgressJobIds } = useJobsContext();
  const { path } = useRouteMatch();
  const { hasEditPermission } = useMe();

  const { searchParams: filters, navigate } =
    useSearchParams(surveysFiltersSchema);

  const { data: surveys, isLoading } = useSurveys({
    ...filters,
    type: 'training',
  });

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('surveys.type.training.title'),
          },
        ]}
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'trainings',
                label: t('surveys.type.training.title'),
                url: ROUTES.surveys.training.list,
                isActive: path === ROUTES.surveys.training.list,
              },
            ]}
          />
        }
      >
        <TableWrapper>
          <TableToolbar>
            <Stack gap={1}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
              >
                <ActionsMenu
                  type="spread"
                  items={[
                    {
                      label: t('components.file_input.generate_ai'),
                      icon: AutoAwesomeOutlined,
                      color: 'textPrimary',
                      disabled:
                        !hasEditPermission(['trainings']) ||
                        inProgressJobIds.length > 0,
                      tooltip:
                        inProgressJobIds.length > 0
                          ? t('trainings.generate_blocked.message')
                          : undefined,
                      onClick: () => {
                        openModal({
                          component: GenerateTrainingModal,
                          size: 'xl',
                        });
                      },
                    },
                    {
                      label: t('trainings.actions.create'),
                      color: 'primary',
                      disabled: !hasEditPermission(['trainings']),
                      onClick: () => {
                        openModal({
                          component: CreateSurveyModal,
                          props: {
                            type: 'training',
                            title: t('trainings.actions.create'),
                          },
                        });
                      },
                    },
                  ]}
                />
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <ListTotal total={surveys?.total} />

                <Stack direction="row" alignItems="center" gap={2}>
                  <Filters
                    value={filters}
                    initialValues={surveysFiltersSchema.parse({})}
                    onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                  >
                    {({ value: values, initialValues, setFilter }) => (
                      <>
                        <FilterButton
                          label={t('common.status')}
                          value={values.status}
                          initialValue={initialValues.status}
                          onChange={(v) => setFilter('status', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                              options={surveyStatuses.map((status) => ({
                                key: status,
                                label: t(`status.${status}`),
                              }))}
                              searchable={false}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('news.published_from')}
                          value={values.publishedAt}
                          initialValue={initialValues.publishedAt}
                          onChange={(v) => setFilter('publishedAt', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <DateRangeFilter
                              value={value}
                              onApply={applyFilter}
                              onClear={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>
                      </>
                    )}
                  </Filters>
                </Stack>
              </Stack>
            </Stack>
          </TableToolbar>

          <Table
            data={surveys?.items ?? []}
            isLoading={isLoading}
            columns={[
              {
                id: 'name',
                header: t('surveys.systemName'),
                cell: ({ row: { original: survey } }) => {
                  return (
                    <Link
                      label={survey.name}
                      to={
                        ['in_progress', 'ended'].includes(survey.status)
                          ? ROUTES.surveys[survey.type].results.replace(
                              ':id',
                              survey.id,
                            )
                          : ROUTES.surveys[survey.type].details.replace(
                              ':id',
                              survey.id,
                            )
                      }
                    />
                  );
                },
                meta: { sort: true },
              },
              {
                id: 'status',
                header: t('common.status'),
                cell: ({ row: { original: survey } }) => {
                  return (
                    <Typography color={COLOR_BY_STATUS[survey.status]}>
                      {t(`surveys.status-${survey.status}`)}
                    </Typography>
                  );
                },
                meta: { sort: true },
              },
              {
                id: 'publishDate',
                header: t('surveys.publishDateTime'),
                cell: ({ row: { original: survey } }) => {
                  return survey.publishDateTime
                    ? moment(survey.publishDateTime).format('l LT')
                    : null;
                },
                meta: { sort: true },
              },
              {
                id: 'currentRecipients',
                header: t('common.recipients'),
                meta: { align: 'right' },
                cell: ({ row: { original: survey } }) => {
                  return survey.currentRecipients;
                },
              },
              {
                id: 'completedBy',
                header: t('surveys.completedBy'),
                meta: { align: 'right' },
                cell: ({ row: { original: survey } }) => {
                  return survey.completedBy;
                },
              },
              {
                id: 'responseRate',
                header: t('surveys.responseRate'),
                cell: ({ row: { original: survey } }) => {
                  return survey.currentRecipients
                    ? formatPercents(
                        i18n.language,
                        survey.completedBy / survey.currentRecipients,
                      )
                    : null;
                },
                meta: { align: 'right' },
              },
              {
                id: 'actions',
                cell: ({ row: { original: survey } }) => {
                  return <SurveyActions type="compact" survey={survey} />;
                },
              },
            ]}
            sort={{
              value: filters.sort,
              onChange: (v) => navigate({ params: { sort: v } }),
            }}
            pagination={
              surveys && {
                count: surveys.total,
                page: filters.page,
                pageSize: filters.pageSize,
                pageSizeOptions: APP.pagination.defaults.pageSize,
                onChange: (page, pageSize) => {
                  navigate({ params: { page, pageSize } });
                },
              }
            }
          />
        </TableWrapper>
      </PageContentLayout>
    </Stack>
  );
};
